import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  cancelRun,
  RunStartError,
  startRun,
  subscribeToRun,
  type LogLine,
  type RunOptions,
  type RunStatus,
  type RunSummary,
} from "../lib/runs";
import { planLabel, useAuth } from "../lib/auth";
import {
  BillingError,
  listAuthStates,
  postBillingCheckout,
  type AuthStateSummary,
} from "../lib/api";
import { AuthStateCaptureDialog } from "../components/AuthStateCaptureDialog";

/**
 * Upsell shape for a rejected start. Keeps the UI's state simple — one
 * optional "why was I blocked" object — instead of juggling per-code flags.
 */
interface GateBlock {
  code: string;
  message: string;
  /** Which CTA to render. Maps ~1:1 onto the server's rejection codes. */
  kind: "sign-in" | "upgrade" | "switch-host" | "retry-tomorrow";
}

/**
 * Kebab-case framework keys — must match `Framework` in `apps/api/src/billing/tiers.ts`
 * and the `--emit` flag values exactly (both sides do a literal `includes`).
 */
type FrameworkKey = NonNullable<RunOptions["emit"]>[number];

/**
 * Catalog used by the emit checkbox list. Ordered Free → Starter → Pro so the
 * always-available option shows first. `upgradeHint` is rendered under a
 * disabled checkbox so the user sees what plan unlocks each entry.
 */
const EMIT_FRAMEWORKS: ReadonlyArray<{
  key: FrameworkKey;
  label: string;
  upgradeHint: string;
}> = [
  { key: "playwright-ts", label: "Playwright TS", upgradeHint: "Available on every plan" },
  { key: "selenium-java", label: "Selenium Java (JUnit 5)", upgradeHint: "Starter+" },
  { key: "selenium-python", label: "Selenium Python (pytest)", upgradeHint: "Starter+" },
  { key: "playwright-java", label: "Playwright Java (JUnit 5)", upgradeHint: "Starter+" },
  { key: "playwright-python", label: "Playwright Python (pytest)", upgradeHint: "Starter+" },
  { key: "cypress-ts", label: "Cypress TS", upgradeHint: "Pro+" },
  { key: "uipath", label: "UiPath (XAML)", upgradeHint: "Pro+" },
];

/**
 * True if the entitlement permits emitting `key`. Wildcard `"*"` matches
 * everything. Kept in sync with `isFrameworkAllowed` in the API so the
 * client-side picker's enablement mirrors what the server will accept.
 */
function isEmitAllowed(
  allowedEmits: ReadonlyArray<string>,
  key: FrameworkKey,
): boolean {
  return allowedEmits.includes("*") || allowedEmits.includes(key);
}

/**
 * "New Crawl" screen. Replaces the `pnpm uaip crawl ...` terminal command
 * with a form + live log stream, so QA users who don't live in a shell can
 * still drive UAIP end-to-end.
 *
 * Flow:
 *   1. User fills the form, clicks "Start crawl".
 *   2. POST /api/runs with the options → server spawns the `uaip` CLI.
 *   3. We open an EventSource on /api/runs/:id/events and stream log lines
 *      into the terminal panel.
 *   4. On "done", a "View crawl" button appears that deep-links to
 *      /crawls/:outDir so the Crawl Map picks up the fresh output.
 */
export function CrawlRunPage(): JSX.Element {
  const navigate = useNavigate();
  const { user, entitlements, disabled: authDisabled, refresh: refreshAuth } = useAuth();

  // Anonymous users get a lower page ceiling; pick a sensible default
  // that respects their entitlement so the first click isn't an error.
  const defaultMaxPages = entitlements.maxPagesPerCrawl > 0
    ? Math.min(8, entitlements.maxPagesPerCrawl)
    : 8;

  // ─── Form state ───
  const [url, setUrl] = useState("https://demoblaze.com/");
  const [maxPages, setMaxPages] = useState(defaultMaxPages);
  // Seed the selected-emitters set with the first framework the current tier
  // allows. That way a fresh Guest/Free visitor lands on a submittable form
  // (playwright-ts) instead of an always-rejected Selenium default.
  const [selectedEmits, setSelectedEmits] = useState<Set<FrameworkKey>>(() => {
    const firstAllowed = EMIT_FRAMEWORKS.find((f) =>
      isEmitAllowed(entitlements.allowedEmits, f.key),
    );
    return new Set<FrameworkKey>(firstAllowed ? [firstAllowed.key] : []);
  });
  const [semantic, setSemantic] = useState(false);
  const [hud, setHud] = useState(false);
  /**
   * Required affirmation: caller asserts they have permission to crawl
   * the target site. Server enforces this via 400 PERMISSION_NOT_AFFIRMED
   * — we mirror the gate client-side so the user gets immediate feedback
   * rather than a roundtrip-and-error flow. Defaults to false; the user
   * has to tick the checkbox each crawl. We considered remembering the
   * choice across sessions and rejected it: a checkbox they tick once
   * and forget about isn't doing the click-through liability shift work
   * we want it to do.
   */
  const [permissionAffirmed, setPermissionAffirmed] = useState(false);
  const [authPath, setAuthPath] = useState("");
  // Plan §22 Login UX: vault-based auth states. Replaces the legacy
  // path-string input. authStateId wins over authPath when both are
  // set (vault is the modern flow; raw path stays as a CLI fallback).
  const [authStateId, setAuthStateId] = useState<string>("");
  const [authStates, setAuthStates] = useState<AuthStateSummary[] | null>(null);
  const [authStatesErr, setAuthStatesErr] = useState<string | null>(null);
  const [captureDialogOpen, setCaptureDialogOpen] = useState(false);

  function toggleEmit(key: FrameworkKey, on: boolean): void {
    setSelectedEmits((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  // Gate detail surfaced from the API (402/403/429 responses). Richer than
  // a generic form error so we can render an upsell/CTA.
  const [gateBlock, setGateBlock] = useState<GateBlock | null>(null);

  // ─── Run state ───
  const [run, setRun] = useState<RunSummary | null>(null);
  const [status, setStatus] = useState<RunStatus | "idle">("idle");
  const [outDir, setOutDir] = useState<string | null>(null);
  const [logs, setLogs] = useState<LogLine[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  // Scroll the log panel to the bottom on new lines, but only when the user
  // is already near the bottom — let them scroll back without being yanked.
  const logPanelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = logPanelRef.current;
    if (!el) return;
    const atBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (atBottom) el.scrollTop = el.scrollHeight;
  }, [logs]);

  // Plan §22: load the user's saved auth states so the picker has
  // options. Tier-gated server-side; we only fetch when the entitlement
  // says it's allowed (Free+ all qualify).
  useEffect(() => {
    if (!entitlements.allowAuthedCrawl) return;
    let alive = true;
    listAuthStates()
      .then((rows) => {
        if (alive) setAuthStates(rows);
      })
      .catch((err) => {
        if (alive) {
          setAuthStates([]);
          setAuthStatesErr(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      alive = false;
    };
  }, [entitlements.allowAuthedCrawl]);

  // Clean up SSE subscription on unmount.
  const unsubRef = useRef<null | (() => void)>(null);
  useEffect(() => {
    return () => {
      unsubRef.current?.();
    };
  }, []);

  function resetForNewRun(): void {
    setLogs([]);
    setRun(null);
    setStatus("idle");
    setOutDir(null);
    setFormError(null);
    setGateBlock(null);
    unsubRef.current?.();
    unsubRef.current = null;
  }

  function gateBlockFromError(err: RunStartError): GateBlock {
    // Map server codes onto a CTA kind. Fallthrough = generic upgrade CTA
    // so we still give the user somewhere to go.
    switch (err.code) {
      case "AUTH_REQUIRED":
        return { code: err.code, message: err.message, kind: "sign-in" };
      case "ANON_DAILY_LIMIT":
        return { code: err.code, message: err.message, kind: "sign-in" };
      case "HOST_NOT_ALLOWED":
        return { code: err.code, message: err.message, kind: "switch-host" };
      case "MONTHLY_LIMIT":
        return { code: err.code, message: err.message, kind: "upgrade" };
      default:
        return {
          code: err.code ?? `HTTP_${err.status}`,
          message: err.message,
          kind: err.status === 429 ? "retry-tomorrow" : "upgrade",
        };
    }
  }

  async function handleStart(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    resetForNewRun();

    if (selectedEmits.size === 0) {
      setFormError("pick at least one emit target");
      return;
    }

    if (!permissionAffirmed) {
      setFormError(
        "Tick the permission box to confirm you have authorisation to crawl this site.",
      );
      return;
    }

    // Preserve the catalog order so the server log reads top-to-bottom as the
    // checklist does, rather than by insertion order into the Set.
    const emit: RunOptions["emit"] = EMIT_FRAMEWORKS.filter((f) =>
      selectedEmits.has(f.key),
    ).map((f) => f.key);

    const opts: RunOptions = {
      url: url.trim(),
      maxPages: Number(maxPages),
      emit,
      semantic,
      hud,
      permissionAffirmed: true,
    };
    // Vault-saved auth wins over a literal path (path stays as a CLI
    // escape hatch). Server enforces the same precedence.
    if (authStateId.trim()) opts.authStateId = authStateId.trim();
    else if (authPath.trim()) opts.authPath = authPath.trim();

    let started: RunSummary;
    try {
      started = await startRun(opts);
    } catch (err) {
      if (err instanceof RunStartError && [401, 402, 403, 429].includes(err.status)) {
        setGateBlock(gateBlockFromError(err));
        // A 401 likely means our cached "authed" state is stale — refresh
        // so the TopNav + teaser react to the truth.
        if (err.status === 401) void refreshAuth();
      } else {
        setFormError((err as Error).message);
      }
      return;
    }
    setRun(started);
    setStatus(started.status);
    setOutDir(started.outDir);

    unsubRef.current = subscribeToRun(started.id, {
      onHello: (h) => {
        setOutDir(h.outDir);
        setStatus(h.status);
      },
      onLog: (line) => setLogs((prev) => [...prev, line]),
      onStatus: (s) => setStatus(s),
      onDone: (d) => {
        setStatus(d.status);
        setOutDir(d.outDir);
      },
      onError: (err) => {
        setLogs((prev) => [
          ...prev,
          { at: new Date().toISOString(), stream: "system", line: err.message },
        ]);
      },
    });
  }

  async function handleCancel(): Promise<void> {
    if (!run) return;
    try {
      await cancelRun(run.id);
    } catch (err) {
      setFormError((err as Error).message);
    }
  }

  const busy = status === "running";
  const canView = status === "done" && outDir !== null;

  // Show the anonymous teaser only when auth is actually enabled on the API
  // AND the visitor isn't signed in. Authed users already know their tier
  // from the TopNav badge.
  const showAnonTeaser = !authDisabled && !user;

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">New Crawl</h1>
          <p className="text-sm text-slate-400 mt-1">
            Point UAIP at a URL; this replaces{" "}
            <code className="text-slate-300">pnpm uaip crawl</code> so you can
            drive the whole pipeline from the dashboard.
          </p>
        </div>
        <Link
          to="/"
          className="text-sm text-slate-400 hover:text-white"
        >
          ← back to dashboard
        </Link>
      </header>

      {showAnonTeaser && <AnonTeaser entitlements={entitlements} />}
      {user && entitlements.plan === "free" && (
        <TierHint
          text={
            `Free plan: ${entitlements.crawlsPerMonth} crawls/month, ` +
            `${entitlements.maxPagesPerCrawl} pages/crawl, 1 project, Playwright TS only. ` +
            `Upgrade to Starter for 3 projects, 250 pages/crawl, Selenium + Playwright (Java/Python), daily schedules, and Slack alerts — ` +
            `or jump to Pro for 10 projects, 2000 pages/crawl, all 7 emitters, arbitrary cron, self-heal, Slack + Jira.`
          }
        />
      )}
      {user && entitlements.plan === "starter" && (
        <TierHint
          text={
            `Starter plan: ${entitlements.crawlsPerMonth} crawls/month, ` +
            `${entitlements.maxPagesPerCrawl} pages/crawl, 3 projects, Playwright TS + Selenium + Playwright (Java/Python), daily schedules, Slack alerts. ` +
            `Upgrade to Pro for 10 projects, 2000 pages/crawl, all 7 emitters (adds Cypress + UiPath), arbitrary cron, self-heal, Slack + Jira.`
          }
        />
      )}
      {gateBlock && <GateBlockBanner block={gateBlock} onDismiss={() => setGateBlock(null)} />}

      <form
        onSubmit={handleStart}
        className="rounded-xl border border-slate-800 bg-slate-900/50 p-5 space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-[1fr_160px]">
          <Field label="Target URL">
            <input
              type="url"
              required
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/"
              disabled={busy}
              className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm font-mono focus:border-indigo-500 focus:outline-none disabled:opacity-60"
            />
          </Field>
          <Field label="Max pages">
            <input
              type="number"
              min={1}
              max={entitlements.maxPagesPerCrawl > 0 ? entitlements.maxPagesPerCrawl : 50}
              required
              value={maxPages}
              onChange={(e) => setMaxPages(Number(e.target.value))}
              disabled={busy}
              className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm font-mono focus:border-indigo-500 focus:outline-none disabled:opacity-60"
            />
            {entitlements.maxPagesPerCrawl > 0 && (
              <p className="mt-1 text-xs text-slate-500">
                Capped at {entitlements.maxPagesPerCrawl} on your{" "}
                {planLabel(entitlements.plan)} plan.
              </p>
            )}
          </Field>
        </div>

        <Field label="Emit">
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {EMIT_FRAMEWORKS.map((fw) => {
              const allowed = isEmitAllowed(entitlements.allowedEmits, fw.key);
              const checkProps: {
                checked: boolean;
                onChange: (v: boolean) => void;
                label: string;
                disabled: boolean;
                hint?: string;
              } = {
                checked: selectedEmits.has(fw.key) && allowed,
                onChange: (on) => toggleEmit(fw.key, on),
                disabled: busy || !allowed,
                label: fw.label,
              };
              if (!allowed) {
                checkProps.hint = `${fw.upgradeHint} — your ${planLabel(entitlements.plan)} plan excludes this`;
              }
              return <Check key={fw.key} {...checkProps} />;
            })}
          </div>
        </Field>

        <Field label="Options">
          <div className="flex flex-wrap gap-4 text-sm">
            {/* Semantic labelling is only shown when the deployment enables
                the Ollama-backed feature (UAIP_ENABLE_SEMANTIC). Hidden by
                default so users can't request a feature we don't run. */}
            {entitlements.allowSemantic && (
              <Check
                checked={semantic}
                onChange={setSemantic}
                disabled={busy}
                label="Semantic labels (Ollama)"
                hint="Requires Ollama running locally"
              />
            )}
            <Check
              checked={hud && entitlements.allowHud}
              onChange={setHud}
              disabled={busy || !entitlements.allowHud}
              label="Live HUD overlay"
              hint={
                entitlements.allowHud
                  ? "Shows discovered elements in the browser"
                  : `Pro feature — your ${planLabel(entitlements.plan)} plan excludes this`
              }
            />
          </div>
        </Field>

        <Field label="Login required? (optional)">
          {!entitlements.allowAuthedCrawl ? (
            <div className="text-xs text-slate-500">
              Sign in to enable authenticated crawls.
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={authStateId}
                  onChange={(e) => setAuthStateId(e.target.value)}
                  disabled={busy || authStates === null}
                  className="flex-1 rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none disabled:opacity-60"
                >
                  <option value="">No login — public crawl</option>
                  {authStates?.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label} · {s.host}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setCaptureDialogOpen(true)}
                  disabled={busy}
                  className="rounded-md border border-indigo-600/60 bg-indigo-700/20 px-3 py-2 text-sm text-indigo-200 hover:bg-indigo-700/40 disabled:opacity-50"
                >
                  + Capture new login
                </button>
              </div>
              {authStatesErr !== null && (
                <p className="text-xs text-rose-400">
                  Couldn&apos;t load saved logins: {authStatesErr}
                </p>
              )}
              {authStates !== null && authStates.length === 0 && (
                <p className="text-xs text-slate-500">
                  No saved logins yet. Click <em>Capture new login</em> to
                  record a session for an authenticated site.
                </p>
              )}
              <p className="text-xs text-slate-500">
                Sessions are encrypted at rest (AES-GCM). Use a dedicated
                test account, never your prod password.
              </p>
            </div>
          )}
        </Field>

        {/* Permission affirmation. Required before every interactive crawl
         *  start. Server enforces the same gate via 400 PERMISSION_NOT_AFFIRMED. */}
        <label className="flex cursor-pointer items-start gap-2 rounded-md border border-amber-800/40 bg-amber-900/10 px-3 py-2 text-xs text-amber-100">
          <input
            type="checkbox"
            checked={permissionAffirmed}
            onChange={(e) => setPermissionAffirmed(e.target.checked)}
            disabled={busy}
            className="mt-0.5 h-4 w-4 rounded border-amber-700 bg-slate-900 text-amber-500 focus:ring-amber-500"
          />
          <span>
            <strong className="text-amber-200">I have permission to crawl this site.</strong>{" "}
            UAIP only supports automated testing of sites you own or have explicit
            authorization to scan. Crawling without permission may violate
            terms of service and applicable law.
          </span>
        </label>

        {formError && (
          <div className="rounded-md border border-rose-700 bg-rose-950/40 px-3 py-2 text-sm text-rose-200">
            {formError}
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={busy || !permissionAffirmed}
            title={!permissionAffirmed ? "Confirm permission to crawl this site first" : ""}
            className="rounded-md bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-400 disabled:opacity-50"
          >
            {busy ? "Crawling…" : "Start crawl"}
          </button>
          {busy && (
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-md border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800"
            >
              Cancel
            </button>
          )}
          {canView && outDir && (
            <button
              type="button"
              onClick={() => navigate(`/crawls/${encodeURIComponent(outDir)}`)}
              className="rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-400"
            >
              View crawl →
            </button>
          )}
          <StatusPill status={status} />
        </div>
      </form>

      <section className="rounded-xl border border-slate-800 bg-slate-950/70">
        <header className="flex items-center justify-between border-b border-slate-800 px-4 py-2">
          <div className="text-sm text-slate-400">Live log</div>
          <div className="text-xs text-slate-500">
            {logs.length} line{logs.length === 1 ? "" : "s"}
            {outDir && <> · out: <code className="text-slate-400">{outDir}</code></>}
          </div>
        </header>
        <div
          ref={logPanelRef}
          className="h-[420px] overflow-auto p-4 font-mono text-xs leading-relaxed"
        >
          {logs.length === 0 ? (
            <div className="text-slate-500">
              (no output yet — start a crawl to see progress here)
            </div>
          ) : (
            logs.map((line, i) => (
              <div
                key={i}
                className={
                  line.stream === "stderr"
                    ? "text-rose-300"
                    : line.stream === "system"
                      ? "text-indigo-300"
                      : "text-slate-200"
                }
              >
                {line.line || "\u00a0"}
              </div>
            ))
          )}
        </div>
      </section>

      {captureDialogOpen && (
        <AuthStateCaptureDialog
          defaultLoginUrl={url}
          onClose={() => setCaptureDialogOpen(false)}
          onCaptured={(state) => {
            setCaptureDialogOpen(false);
            // Insert the new state at the head of the list and auto-select it.
            setAuthStates((prev) => (prev ? [state, ...prev] : [state]));
            setAuthStateId(state.id);
          }}
        />
      )}
    </div>
  );
}

function Field(props: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="text-xs font-medium uppercase tracking-wider text-slate-400">
        {props.label}
      </span>
      <div className="mt-1">{props.children}</div>
    </label>
  );
}

function Check(props: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
  hint?: string;
}): JSX.Element {
  return (
    <label
      className={`inline-flex items-start gap-2 cursor-pointer ${
        props.disabled ? "opacity-60 cursor-not-allowed" : ""
      }`}
    >
      <input
        type="checkbox"
        checked={props.checked}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.target.checked)}
        className="mt-[3px] h-4 w-4 rounded border-slate-600 bg-slate-900 text-indigo-500 focus:ring-indigo-500"
      />
      <span>
        <span className="text-slate-200">{props.label}</span>
        {props.hint && (
          <span className="block text-xs text-slate-500">{props.hint}</span>
        )}
      </span>
    </label>
  );
}

function StatusPill(props: { status: RunStatus | "idle" }): JSX.Element {
  const { status } = props;
  const styles: Record<typeof status, string> = {
    idle: "bg-slate-800 text-slate-400",
    running: "bg-indigo-900/50 text-indigo-200",
    done: "bg-emerald-900/40 text-emerald-200",
    failed: "bg-rose-900/40 text-rose-200",
    cancelled: "bg-amber-900/40 text-amber-200",
  };
  return (
    <span className={`rounded-full px-3 py-1 text-xs ${styles[status]}`}>
      {status}
    </span>
  );
}

/**
 * Anonymous "try before you buy" banner. Shows the exact caps the API will
 * enforce so a new visitor doesn't have to click Submit to discover them.
 */
function AnonTeaser(props: {
  entitlements: import("../lib/api").Entitlements;
}): JSX.Element {
  const redirect = "/crawls/new";
  return (
    <div className="flex items-start gap-3 rounded-xl border border-indigo-800/70 bg-indigo-950/40 p-4">
      <div className="mt-0.5 text-indigo-300" aria-hidden>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 5a1 1 0 110 2 1 1 0 010-2zm2 11h-4v-1h1v-4h-1v-1h3v5h1v1z" />
        </svg>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-indigo-100">
          You're crawling as a guest.
        </div>
        <p className="mt-0.5 text-xs text-indigo-200/80">
          Guests get {props.entitlements.crawlsPerDay} crawls/day,{" "}
          {props.entitlements.maxPagesPerCrawl} pages per crawl, and can't use
          the live HUD.{" "}
          <Link
            to={`/sign-in?redirect=${encodeURIComponent(redirect)}`}
            className="font-medium text-white underline decoration-dotted underline-offset-2 hover:decoration-solid"
          >
            Sign in for free
          </Link>{" "}
          to raise the limits.
        </p>
      </div>
    </div>
  );
}

function TierHint(props: { text: string }): JSX.Element {
  return (
    <div className="rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2 text-xs text-slate-400">
      {props.text}
    </div>
  );
}

/**
 * Banner rendered when the API refuses a start request because of plan
 * limits. Inline CTA matches the rejection shape so the next click is
 * always the right next step.
 */
function GateBlockBanner(props: {
  block: GateBlock;
  onDismiss: () => void;
}): JSX.Element {
  const { block } = props;
  const border =
    block.kind === "sign-in"
      ? "border-indigo-800/70 bg-indigo-950/40 text-indigo-100"
      : block.kind === "upgrade"
        ? "border-amber-800/70 bg-amber-950/40 text-amber-100"
        : block.kind === "switch-host"
          ? "border-rose-800/70 bg-rose-950/40 text-rose-100"
          : "border-slate-700 bg-slate-900 text-slate-200";

  return (
    <div className={`flex items-start justify-between gap-3 rounded-xl border p-4 ${border}`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">We couldn't start that crawl.</div>
        <p className="mt-0.5 text-xs opacity-90">{block.message}</p>
        <div className="mt-2">
          <GateCta kind={block.kind} />
        </div>
      </div>
      <button
        type="button"
        onClick={props.onDismiss}
        aria-label="Dismiss"
        className="rounded-md px-2 py-1 text-xs text-slate-300 hover:bg-white/10"
      >
        ✕
      </button>
    </div>
  );
}

function GateCta(props: { kind: GateBlock["kind"] }): JSX.Element | null {
  switch (props.kind) {
    case "sign-in":
      return (
        <Link
          to={`/sign-in?redirect=${encodeURIComponent("/crawls/new")}`}
          className="inline-flex rounded-md bg-white/10 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/20"
        >
          Sign in to continue
        </Link>
      );
    case "upgrade":
      return <UpgradeCta />;
    case "switch-host":
      return (
        <Link
          to="/account"
          className="inline-flex rounded-md bg-white/10 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/20"
        >
          Manage host
        </Link>
      );
    case "retry-tomorrow":
    default:
      return null;
  }
}

/**
 * Upgrade CTA inside the gate banner — kicks off Stripe Checkout inline so
 * a user who just hit MONTHLY_LIMIT can convert without bouncing through
 * /account first.
 *
 * Error codes map to three buckets:
 *   - BILLING_DISABLED / BILLING_CONFIG_INCOMPLETE → fall back to the marketing
 *     pricing page so self-hosted / pre-launch deploys still have a next step.
 *   - AUTH_REQUIRED → push the user to /sign-in (shouldn't normally happen
 *     here since the upgrade gate implies an authed user, but harmless).
 *   - ALREADY_SUBSCRIBED → direct them to /account to manage instead.
 *   - Anything else → show the server message inline with a Retry.
 */
function UpgradeCta(): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ kind: "fallback" | "auth" | "manage" | "retry"; message: string } | null>(null);

  async function handleClick(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const res = await postBillingCheckout();
      window.location.assign(res.url);
    } catch (err) {
      setBusy(false);
      if (err instanceof BillingError) {
        if (err.code === "BILLING_DISABLED" || err.code === "BILLING_CONFIG_INCOMPLETE") {
          setError({ kind: "fallback", message: "Billing isn't available on this deployment." });
        } else if (err.code === "AUTH_REQUIRED") {
          setError({ kind: "auth", message: "Sign in to continue." });
        } else if (err.code === "ALREADY_SUBSCRIBED") {
          setError({ kind: "manage", message: "You're already on Pro." });
        } else {
          setError({ kind: "retry", message: err.message || "Couldn't start checkout." });
        }
      } else {
        setError({ kind: "retry", message: "Couldn't start checkout. Try again." });
      }
    }
  }

  if (error?.kind === "fallback") {
    return (
      <a
        href="https://uaip.dev/pricing"
        target="_blank"
        rel="noreferrer"
        className="inline-flex rounded-md bg-white/10 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/20"
      >
        See Pro plan
      </a>
    );
  }
  if (error?.kind === "auth") {
    return (
      <Link
        to={`/sign-in?redirect=${encodeURIComponent("/crawls/new")}`}
        className="inline-flex rounded-md bg-white/10 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/20"
      >
        Sign in to continue
      </Link>
    );
  }
  if (error?.kind === "manage") {
    return (
      <Link
        to="/account"
        className="inline-flex rounded-md bg-white/10 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/20"
      >
        Manage billing
      </Link>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        className="inline-flex w-fit items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 shadow-sm hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {busy ? "Opening checkout…" : "Upgrade to Pro"}
      </button>
      {error?.kind === "retry" ? (
        <span className="text-[11px] text-amber-200/90">{error.message}</span>
      ) : null}
    </div>
  );
}
